import type { NetGame } from '@orb/net';
import { Sim, type Action, type Input, type SimEvent, type SimSetup } from '@orb/sim';

/** Something that follows the simulation: sees every tick's events, then advances its tick-rate visuals. */
export interface SimView {
  events?(events: readonly SimEvent[]): void;
  tick?(): void;
  /** The state changed without events (a quiet catch-up or a restored snapshot): rebuild from it. */
  resync?(): void;
}

/**
 * Runs the deterministic simulation for the client. Offline, local actions are applied on the next
 * tick. Online, actions go to the server and the sim plays exactly the server's turns, waiting when
 * the next one has not arrived.
 */
export class Driver {
  readonly sim: Sim;
  /** The seat this client plays. */
  readonly me: number;
  readonly net: NetGame | null;
  /** Online: whether the last loop step could run a tick (render interpolation holds when it couldn't). */
  private moved = true;
  private pending: Action[] = [];
  private readonly inputs: Input[] = [];
  private acc = 0;
  private readonly views: SimView[] = [];

  constructor(setup: SimSetup, online?: { net: NetGame; seat: number }) {
    this.sim = Sim.create(setup);
    this.net = online?.net ?? null;
    this.me = online?.seat ?? 0;
  }

  get state() { return this.sim.state; }
  get player() { return this.sim.state.players[this.me]; }
  get layout() { return this.sim.layout; }

  follow(...views: SimView[]) { this.views.push(...views); }

  send(action: Action) {
    if (this.net) this.net.send(action, this.sim); else this.pending.push(action);
  }

  /**
   * Called once per 60 Hz loop step. `rate` < 1 is the local hit-stop / slow motion: ticks still
   * happen whole (the sim never sees a fractional step), just less often. Only solo may slow its sim:
   * in co-op every client runs the same ticks, so hit-stop there is purely visual.
   */
  advance(rate = 1) {
    if (this.net) { this.moved = this.net.advance(this.sim, (events) => this.dispatch(events), () => this.resync()) > 0; return; }
    const r = this.rate(rate);
    // back at full speed, drop the fraction left over from a slow-down: carried on, it would pin the
    // render interpolation against 1 for part of every tick (visible judder for the rest of the game)
    if (r >= 1) this.acc = 0;
    this.acc += r;
    while (this.acc >= 1 - 1e-9) { this.acc -= 1; this.tick(); }
  }

  /** Render interpolation between the last two sim ticks. */
  alpha(loopAlpha: number, rate: number) {
    if (this.net) return this.moved ? loopAlpha : 1;
    return Math.min(1, Math.max(0, this.acc + loopAlpha * this.rate(rate)));
  }

  /** Only an offline solo game may slow its sim (hit-stop); everyone else runs the agreed ticks. */
  private rate(rate: number) { return this.sim.state.mode === 'solo' && !this.net ? Math.min(1, Math.max(0, rate)) : 1; }

  private tick() {
    const inputs = this.inputs; inputs.length = 0;
    for (const action of this.pending) inputs.push({ player: this.me, action });
    this.pending.length = 0;
    this.dispatch(this.sim.step(inputs));
  }

  private resync() {
    for (const v of this.views) { v.resync?.(); v.tick?.(); }
  }

  private dispatch(events: readonly SimEvent[]) {
    for (const v of this.views) v.events?.(events);
    for (const v of this.views) v.tick?.();
  }
}
